import { supabase } from '../utils/supabase';
import { buildLeaguePlayerAnalysis, buildLeagueTeamRoster } from '../utils/leaguePlayerAnalysis';
import { buildLinkedClubAppearanceMatches } from '../utils/leagueTeamSelection';

const SOURCE_SLUG = 'mcba';

const throwIf = (error, fallback) => {
  if (error) throw new Error(error.message || fallback);
};

export const fetchMiddlesexSeasons = async () => {
  const { data: source, error: sourceError } = await supabase
    .from('league_sources')
    .select('id')
    .eq('slug', SOURCE_SLUG)
    .maybeSingle();
  throwIf(sourceError, 'Could not load league source');
  if (!source) return [];
  const { data, error } = await supabase
    .from('league_seasons')
    .select('id, start_year, label, scraped_at')
    .eq('source_id', source.id)
    .order('start_year', { ascending: false });
  throwIf(error, 'Could not load seasons');
  return data ?? [];
};

export const fetchMiddlesexSeason = async (startYear = 2025) => {
  const { data: source, error: sourceError } = await supabase
    .from('league_sources')
    .select('id, slug, name')
    .eq('slug', SOURCE_SLUG)
    .maybeSingle();
  throwIf(sourceError, 'Could not load league source');
  if (!source) return null;

  const { data: season, error: seasonError } = await supabase
    .from('league_seasons')
    .select('id, start_year, label, scraped_at')
    .eq('source_id', source.id)
    .eq('start_year', startYear)
    .maybeSingle();
  throwIf(seasonError, 'Could not load season');
  if (!season) return null;

  const { data: competitions, error: competitionsError } = await supabase
    .from('league_competitions')
    .select('id, code, label, name, source_league_type_id')
    .eq('season_id', season.id)
    .order('label');
  throwIf(competitionsError, 'Could not load competitions');

  const competitionIds = (competitions ?? []).map((row) => row.id);
  const { data: divisions, error: divisionsError } = competitionIds.length
    ? await supabase
        .from('league_divisions')
        .select('id, name, source_division_id, competition_id')
        .in('competition_id', competitionIds)
        .order('name')
    : { data: [], error: null };
  throwIf(divisionsError, 'Could not load divisions');

  const divisionIds = (divisions ?? []).map((row) => row.id);
  const [{ data: standings, error: standingsError }, { data: teams, error: teamsError }, { data: fixtures, error: fixturesError }] =
    await Promise.all([
      divisionIds.length
        ? supabase
            .from('league_division_standings')
            .select('id, division_id, team_id, position, played, matches_won, matches_drawn, matches_lost, penalties, rubbers_won, rubbers_lost, points_for')
            .in('division_id', divisionIds)
            .order('position')
        : Promise.resolve({ data: [], error: null }),
      divisionIds.length
        ? supabase
            .from('league_teams')
            .select('id, name, source_team_id, league_club_id, division_id')
            .in('division_id', divisionIds)
        : Promise.resolve({ data: [], error: null }),
      supabase
        .from('league_fixtures')
        .select('id, source_fixture_id, match_date, match_time, home_team_id, away_team_id, home_score, away_score, fixture_status, fixture_status_label, card_status, walkover_status, walkover_label, match_status, result_note, division_id, competition_id, card_format')
        .eq('season_id', season.id)
        .order('match_date', { ascending: false }),
    ]);
  throwIf(standingsError, 'Could not load standings');
  throwIf(teamsError, 'Could not load teams');
  throwIf(fixturesError, 'Could not load fixtures');

  const clubIds = [...new Set((teams ?? []).map((team) => team.league_club_id).filter(Boolean))];
  const { data: clubs, error: clubsError } = clubIds.length
    ? await supabase.from('league_clubs').select('id, name, source_club_id').in('id', clubIds)
    : { data: [], error: null };
  throwIf(clubsError, 'Could not load clubs');

  return {
    source,
    season,
    competitions: competitions ?? [],
    divisions: divisions ?? [],
    standings: standings ?? [],
    teams: teams ?? [],
    fixtures: fixtures ?? [],
    clubs: clubs ?? [],
  };
};

export const fetchViewerLeagueAppearances = async (clubId, playerId) => {
  if (!clubId || !playerId) return { fixtureIds: [], teamIds: [] };
  const { data: link, error: linkError } = await supabase
    .from('player_league_links')
    .select('league_player_id')
    .eq('club_id', clubId)
    .eq('player_id', playerId)
    .maybeSingle();
  throwIf(linkError, 'Could not load your league link');
  if (!link?.league_player_id) return { fixtureIds: [], teamIds: [] };

  const { data, error } = await supabase
    .from('league_match_lineups')
    .select('fixture_id, team_id')
    .eq('player_id', link.league_player_id);
  throwIf(error, 'Could not load your appearances');
  return {
    fixtureIds: [...new Set((data ?? []).map((row) => row.fixture_id))],
    teamIds: [...new Set((data ?? []).map((row) => row.team_id).filter(Boolean))],
  };
};

export const fetchViewerPlayedFixtureIds = async (clubId, playerId) => {
  const { fixtureIds } = await fetchViewerLeagueAppearances(clubId, playerId);
  return fixtureIds;
};

export const fetchLeaguePlayerAppearances = async (leaguePlayerId) => {
  const { data: lineups, error: lineupError } = await supabase
    .from('league_match_lineups')
    .select('fixture_id, side, team_id')
    .eq('player_id', leaguePlayerId);
  throwIf(lineupError, 'Could not load appearances');
  const fixtureIds = [...new Set((lineups ?? []).map((row) => row.fixture_id))];
  if (!fixtureIds.length) return { lineups: [], fixtures: [], teams: [] };

  const { data: fixtures, error: fixturesError } = await supabase
    .from('league_fixtures')
    .select('id, source_fixture_id, match_date, match_time, home_team_id, away_team_id, home_score, away_score, walkover_label, competition_id, division_id')
    .in('id', fixtureIds)
    .order('match_date', { ascending: false });
  throwIf(fixturesError, 'Could not load appearance fixtures');

  const teamIds = [...new Set((fixtures ?? []).flatMap((row) => [row.home_team_id, row.away_team_id]).filter(Boolean))];
  const { data: teams, error: teamsError } = teamIds.length
    ? await supabase.from('league_teams').select('id, name, league_club_id').in('id', teamIds)
    : { data: [], error: null };
  throwIf(teamsError, 'Could not load appearance teams');

  return { lineups: lineups ?? [], fixtures: fixtures ?? [], teams: teams ?? [] };
};

export const fetchMatchCard = async (fixtureId) => {
  const [{ data: lineups, error: lineupError }, { data: rubbers, error: rubberError }] = await Promise.all([
    supabase
      .from('league_match_lineups')
      .select('side, slot, player_id, flags, team_id')
      .eq('fixture_id', fixtureId)
      .order('slot'),
    supabase
      .from('league_rubbers')
      .select('id, rubber_number, winner, winner_on_court, result_text, home_player_id_1, home_player_id_2, away_player_id_1, away_player_id_2, home_conceded, away_conceded, single_game, invalid_label, invalid_reason')
      .eq('fixture_id', fixtureId)
      .order('rubber_number'),
  ]);
  throwIf(lineupError, 'Could not load line-ups');
  throwIf(rubberError, 'Could not load rubbers');

  const rubberIds = (rubbers ?? []).map((row) => row.id);
  const { data: games, error: gamesError } = rubberIds.length
    ? await supabase
        .from('league_rubber_games')
        .select('rubber_id, game_number, home_score, away_score')
        .in('rubber_id', rubberIds)
        .order('game_number')
    : { data: [], error: null };
  throwIf(gamesError, 'Could not load rubber games');

  const playerIds = [
    ...(lineups ?? []).map((row) => row.player_id),
    ...(rubbers ?? []).flatMap((row) => [
      row.home_player_id_1,
      row.home_player_id_2,
      row.away_player_id_1,
      row.away_player_id_2,
    ]),
  ].filter(Boolean);
  const uniquePlayerIds = [...new Set(playerIds)];
  const { data: players, error: playersError } = uniquePlayerIds.length
    ? await supabase
        .from('league_players')
        .select('id, display_name, sex_male, source_player_id')
        .in('id', uniquePlayerIds)
    : { data: [], error: null };
  throwIf(playersError, 'Could not load card players');

  const gamesByRubber = new Map();
  for (const game of games ?? []) {
    const list = gamesByRubber.get(game.rubber_id) ?? [];
    list.push({ game: game.game_number, home: game.home_score, away: game.away_score });
    gamesByRubber.set(game.rubber_id, list);
  }

  return {
    lineups: lineups ?? [],
    rubbers: (rubbers ?? []).map((rubber) => ({
      ...rubber,
      games: gamesByRubber.get(rubber.id) ?? [],
    })),
    players: players ?? [],
  };
};

export const searchLeagueClubs = async (query) => {
  const q = String(query ?? '').trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from('league_clubs')
    .select('id, name, source_club_id')
    .ilike('name', `%${q}%`)
    .order('name')
    .limit(20);
  throwIf(error, 'Club search failed');
  return data ?? [];
};

export const fetchClubLeagueLink = async (clubId) => {
  const { data, error } = await supabase
    .from('club_league_links')
    .select('id, club_id, league_club_id, status, created_at, reviewed_at')
    .eq('club_id', clubId)
    .in('status', ['pending', 'approved'])
    .maybeSingle();
  throwIf(error, 'Could not load club league link');
  return data ?? null;
};

export const requestClubLeagueLink = async (clubId, leagueClubId) => {
  const { data, error } = await supabase.rpc('request_club_league_link', {
    p_club_id: clubId,
    p_league_club_id: leagueClubId,
  });
  throwIf(error, 'Could not request league link');
  return data;
};

export const reviewClubLeagueLink = async (linkId, approve) => {
  const { data, error } = await supabase.rpc('review_club_league_link', {
    p_link_id: linkId,
    p_approve: approve,
  });
  throwIf(error, 'Could not review league link');
  return data;
};

export const unlinkClubLeagueLink = async (linkId) => {
  const { error } = await supabase.rpc('unlink_club_league_link', { p_link_id: linkId });
  throwIf(error, 'Could not unlink county club');
};

export const fetchClubLeagueLinksByStatus = async (status) => {
  const { data, error } = await supabase
    .from('club_league_links')
    .select('id, club_id, league_club_id, status, created_at, reviewed_at, requested_by')
    .eq('status', status)
    .order('created_at', { ascending: true });
  throwIf(error, `Could not load ${status} links`);
  const rows = data ?? [];
  const clubIds = [...new Set(rows.map((row) => row.club_id))];
  const leagueClubIds = [...new Set(rows.map((row) => row.league_club_id))];
  const [{ data: clubs }, { data: leagueClubs }] = await Promise.all([
    clubIds.length ? supabase.from('clubs').select('id, name').in('id', clubIds) : { data: [] },
    leagueClubIds.length
      ? supabase.from('league_clubs').select('id, name, source_club_id').in('id', leagueClubIds)
      : { data: [] },
  ]);
  const clubsById = new Map((clubs ?? []).map((row) => [row.id, row]));
  const leagueById = new Map((leagueClubs ?? []).map((row) => [row.id, row]));
  return rows.map((row) => ({
    ...row,
    club: clubsById.get(row.club_id) ?? null,
    leagueClub: leagueById.get(row.league_club_id) ?? null,
  }));
};

export const fetchLinkedClubSeason = async (leagueClubId, startYear = 2025) => {
  const snapshot = await fetchMiddlesexSeason(startYear);
  if (!snapshot) return null;
  const teams = snapshot.teams.filter((team) => team.league_club_id === leagueClubId);
  const teamIds = new Set(teams.map((team) => team.id));
  const fixtures = snapshot.fixtures.filter(
    (fixture) => teamIds.has(fixture.home_team_id) || teamIds.has(fixture.away_team_id),
  );
  return { ...snapshot, teams, allTeams: snapshot.teams, fixtures };
};

export const fetchLeagueClubRoster = async (leagueClubId) => {
  const { data: teams, error: teamsError } = await supabase
    .from('league_teams')
    .select('id')
    .eq('league_club_id', leagueClubId);
  throwIf(teamsError, 'Could not load club teams');
  const teamIds = (teams ?? []).map((row) => row.id);
  if (!teamIds.length) return [];

  const { data: lineups, error: lineupError } = await supabase
    .from('league_match_lineups')
    .select('player_id, team_id, fixture_id')
    .in('team_id', teamIds)
    .not('player_id', 'is', null);
  throwIf(lineupError, 'Could not load roster');

  const counts = new Map();
  for (const row of lineups ?? []) {
    const entry = counts.get(row.player_id) ?? { playerId: row.player_id, matches: new Set() };
    entry.matches.add(row.fixture_id);
    counts.set(row.player_id, entry);
  }
  const playerIds = [...counts.keys()];
  if (!playerIds.length) return [];

  const [{ data: players, error: playersError }, { data: links, error: linksError }] = await Promise.all([
    supabase.from('league_players').select('id, display_name, sex_male, source_player_id').in('id', playerIds),
    supabase
      .from('player_league_links')
      .select('id, player_id, league_player_id')
      .in('league_player_id', playerIds),
  ]);
  throwIf(playersError, 'Could not load roster players');
  throwIf(linksError, 'Could not load player links');

  const linkByLeaguePlayer = new Map((links ?? []).map((row) => [row.league_player_id, row]));
  return (players ?? [])
    .map((player) => ({
      ...player,
      matches: counts.get(player.id)?.matches.size ?? 0,
      link: linkByLeaguePlayer.get(player.id) ?? null,
    }))
    .sort((a, b) => b.matches - a.matches || a.display_name.localeCompare(b.display_name));
};

export const fetchMyClubLeagueSummary = async (clubId, playerId) => {
  const { data: link, error: linkError } = await supabase
    .from('player_league_links')
    .select('league_player_id, league_players:league_player_id (id, display_name, sex_male)')
    .eq('club_id', clubId)
    .eq('player_id', playerId)
    .maybeSingle();
  throwIf(linkError, 'Could not load your league link');
  if (!link) return null;

  const clubLink = await fetchClubLeagueLink(clubId);
  const { lineups, fixtures, teams } = await fetchLeaguePlayerAppearances(link.league_player_id);
  const matches = buildLinkedClubAppearanceMatches({
    fixtures,
    lineups,
    teams,
    leagueClubId: clubLink?.status === 'approved' ? clubLink.league_club_id : null,
  });

  return {
    player: link.league_players,
    matches,
    teamNames: [...new Set(matches.map((match) => match.teamName))],
    won: matches.filter((match) => match.result === 'won').length,
    lost: matches.filter((match) => match.result === 'lost').length,
  };
};

export const fetchMyLeaguePlayerId = async (clubId, playerId) => {
  if (!clubId || !playerId) return null;
  const { data, error } = await supabase
    .from('player_league_links')
    .select('league_player_id')
    .eq('club_id', clubId)
    .eq('player_id', playerId)
    .maybeSingle();
  throwIf(error, 'Could not load your league link');
  return data?.league_player_id ?? null;
};

export const fetchLeagueTeamAnalysisSource = async (teamId, seasonId) => {
  if (!teamId || !seasonId) {
    return { fixtures: [], lineups: [], rubbers: [], players: [], playersById: new Map(), roster: [] };
  }

  const { data: fixtures, error: fixturesError } = await supabase
    .from('league_fixtures')
    .select('id, match_date, match_time, home_team_id, away_team_id, home_score, away_score, walkover_label, walkover_status, card_status, fixture_status, division_id, competition_id')
    .eq('season_id', seasonId)
    .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
    .order('match_date', { ascending: false });
  throwIf(fixturesError, 'Could not load team fixtures');
  const fixtureRows = fixtures ?? [];
  const fixtureIds = fixtureRows.map((row) => row.id);
  if (!fixtureIds.length) {
    return { fixtures: [], lineups: [], rubbers: [], players: [], playersById: new Map(), roster: [] };
  }

  const [{ data: lineups, error: lineupError }, { data: rubbers, error: rubberError }] = await Promise.all([
    supabase
      .from('league_match_lineups')
      .select('fixture_id, side, team_id, player_id, slot')
      .in('fixture_id', fixtureIds)
      .eq('team_id', teamId),
    supabase
      .from('league_rubbers')
      .select('id, fixture_id, rubber_number, winner, winner_on_court, result_text, home_player_id_1, home_player_id_2, away_player_id_1, away_player_id_2, home_conceded, away_conceded, invalid_label')
      .in('fixture_id', fixtureIds)
      .order('rubber_number'),
  ]);
  throwIf(lineupError, 'Could not load team line-ups');
  throwIf(rubberError, 'Could not load team rubbers');

  const rubberRows = rubbers ?? [];
  const rubberIds = rubberRows.map((row) => row.id);
  const { data: games, error: gamesError } = rubberIds.length
    ? await supabase
        .from('league_rubber_games')
        .select('rubber_id, game_number, home_score, away_score')
        .in('rubber_id', rubberIds)
        .order('game_number')
    : { data: [], error: null };
  throwIf(gamesError, 'Could not load rubber games');

  const gamesByRubber = new Map();
  for (const game of games ?? []) {
    const list = gamesByRubber.get(game.rubber_id) ?? [];
    list.push({ home: game.home_score, away: game.away_score });
    gamesByRubber.set(game.rubber_id, list);
  }
  const rubbersWithGames = rubberRows.map((rubber) => ({
    ...rubber,
    games: gamesByRubber.get(rubber.id) ?? [],
  }));

  const playerIds = [
    ...(lineups ?? []).map((row) => row.player_id),
    ...rubbersWithGames.flatMap((row) => [
      row.home_player_id_1,
      row.home_player_id_2,
      row.away_player_id_1,
      row.away_player_id_2,
    ]),
  ].filter(Boolean);
  const uniquePlayerIds = [...new Set(playerIds)];
  const { data: players, error: playersError } = uniquePlayerIds.length
    ? await supabase
        .from('league_players')
        .select('id, display_name, sex_male, source_player_id')
        .in('id', uniquePlayerIds)
    : { data: [], error: null };
  throwIf(playersError, 'Could not load league players');

  const playersById = new Map((players ?? []).map((player) => [player.id, player]));
  return {
    fixtures: fixtureRows,
    lineups: lineups ?? [],
    rubbers: rubbersWithGames,
    players: players ?? [],
    playersById,
    roster: buildLeagueTeamRoster(lineups ?? [], playersById),
  };
};

export const fetchLeaguePlayerAnalysis = async ({ leaguePlayerId, teamId, seasonId, source }) => {
  const data = source ?? await fetchLeagueTeamAnalysisSource(teamId, seasonId);
  if (!leaguePlayerId) {
    return { ...buildLeaguePlayerAnalysis({ leaguePlayerId: null, teamId, ...data }), roster: data.roster };
  }
  return {
    ...buildLeaguePlayerAnalysis({
      leaguePlayerId,
      teamId,
      fixtures: data.fixtures,
      rubbers: data.rubbers,
      lineups: data.lineups,
      playersById: data.playersById,
    }),
    roster: data.roster,
  };
};

const COMPETITION_ORDER = ['open', 'ladies', 'mixed', 'hybrid'];

export const fetchClubLeagueTeamSnapshots = async (clubId) => {
  const clubLink = await fetchClubLeagueLink(clubId);
  if (clubLink?.status !== 'approved' || !clubLink.league_club_id) return [];

  const seasons = await fetchMiddlesexSeasons();
  const season = seasons[0];
  if (!season) return [];

  const { data: competitions, error: competitionsError } = await supabase
    .from('league_competitions')
    .select('id, code, label')
    .eq('season_id', season.id);
  throwIf(competitionsError, 'Could not load competitions');
  const competitionIds = (competitions ?? []).map((row) => row.id);
  if (!competitionIds.length) return [];

  const { data: divisions, error: divisionsError } = await supabase
    .from('league_divisions')
    .select('id, name, competition_id')
    .in('competition_id', competitionIds);
  throwIf(divisionsError, 'Could not load divisions');
  const divisionIds = (divisions ?? []).map((row) => row.id);
  if (!divisionIds.length) return [];

  const { data: teams, error: teamsError } = await supabase
    .from('league_teams')
    .select('id, name, division_id')
    .eq('league_club_id', clubLink.league_club_id)
    .in('division_id', divisionIds);
  throwIf(teamsError, 'Could not load club league teams');
  const teamRows = teams ?? [];
  if (!teamRows.length) return [];

  const { data: standings, error: standingsError } = await supabase
    .from('league_division_standings')
    .select('team_id, position, played, matches_won, matches_lost, points_for')
    .in('team_id', teamRows.map((row) => row.id));
  throwIf(standingsError, 'Could not load standings');

  const competitionsById = new Map((competitions ?? []).map((row) => [row.id, row]));
  const divisionsById = new Map((divisions ?? []).map((row) => [row.id, row]));
  const standingsByTeam = new Map((standings ?? []).map((row) => [row.team_id, row]));

  return teamRows
    .map((team) => {
      const division = divisionsById.get(team.division_id);
      const competition = competitionsById.get(division?.competition_id);
      const standing = standingsByTeam.get(team.id);
      return {
        id: team.id,
        name: team.name,
        leagueName: competition?.label ?? '',
        leagueCode: competition?.code ?? '',
        divisionName: division?.name ?? '',
        position: standing?.position ?? null,
        played: standing?.played ?? null,
        won: standing?.matches_won ?? null,
        lost: standing?.matches_lost ?? null,
        points: standing?.points_for ?? null,
      };
    })
    .sort((a, b) => {
      const order = COMPETITION_ORDER.indexOf(a.leagueCode) - COMPETITION_ORDER.indexOf(b.leagueCode);
      if (order !== 0) return order;
      return (a.position ?? 99) - (b.position ?? 99) || a.name.localeCompare(b.name);
    });
};

export const fetchClubMembersForLinking = async (clubId) => {
  const { data, error } = await supabase
    .from('players_club')
    .select('player_id, players:player_id (id, first_name, last_name, gender)')
    .eq('club_id', clubId)
    .eq('is_active', true);
  throwIf(error, 'Could not load club members');
  return (data ?? [])
    .map((row) => row.players)
    .filter(Boolean)
    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
};

export const linkLeaguePlayer = async (clubId, leaguePlayerId, playerId) => {
  const { data, error } = await supabase.rpc('link_league_player', {
    p_club_id: clubId,
    p_league_player_id: leaguePlayerId,
    p_player_id: playerId,
  });
  throwIf(error, 'Could not link player');
  return data;
};

export const unlinkLeaguePlayer = async (linkId) => {
  const { error } = await supabase.rpc('unlink_league_player', { p_link_id: linkId });
  throwIf(error, 'Could not unlink player');
};

export const isWeakLeagueNameMatch = (leagueName, firstName, lastName) => {
  const leagueLast = String(leagueName ?? '')
    .split(/[\s.]+/)
    .filter(Boolean)
    .pop()
    ?.toLowerCase();
  const memberLast = String(lastName ?? '').toLowerCase();
  if (!leagueLast || !memberLast) return true;
  return !memberLast.startsWith(leagueLast) && !leagueLast.startsWith(memberLast) && !String(firstName ?? '').toLowerCase().includes(leagueLast);
};

const WITHHELD_NAME = 'Withheld player';

export const searchLeaguePlayersForAdmin = async (query) => {
  const q = String(query ?? '').trim();
  if (q.length < 2) return [];
  const { data: players, error } = await supabase
    .from('league_players')
    .select('id, display_name, source_player_id, source_id')
    .ilike('display_name', `%${q}%`)
    .neq('display_name', WITHHELD_NAME)
    .order('display_name')
    .limit(20);
  throwIf(error, 'Player search failed');
  const rows = players ?? [];
  if (!rows.length) return [];

  const playerIds = rows.map((row) => row.id);
  const { data: lineups, error: lineupError } = await supabase
    .from('league_match_lineups')
    .select('player_id, team_id, fixture_id')
    .in('player_id', playerIds)
    .not('team_id', 'is', null);
  throwIf(lineupError, 'Could not load player teams');

  const teamIds = [...new Set((lineups ?? []).map((row) => row.team_id).filter(Boolean))];
  const fixtureIds = [...new Set((lineups ?? []).map((row) => row.fixture_id).filter(Boolean))];

  const [{ data: teams, error: teamsError }, { data: fixtures, error: fixturesError }] = await Promise.all([
    teamIds.length
      ? supabase.from('league_teams').select('id, name, league_club_id').in('id', teamIds)
      : Promise.resolve({ data: [], error: null }),
    fixtureIds.length
      ? supabase.from('league_fixtures').select('id, season_id').in('id', fixtureIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  throwIf(teamsError, 'Could not load player teams');
  throwIf(fixturesError, 'Could not load player fixtures');

  const clubIds = [...new Set((teams ?? []).map((row) => row.league_club_id).filter(Boolean))];
  const seasonIds = [...new Set((fixtures ?? []).map((row) => row.season_id).filter(Boolean))];
  const [{ data: clubs, error: clubsError }, { data: seasons, error: seasonsError }] = await Promise.all([
    clubIds.length
      ? supabase.from('league_clubs').select('id, name').in('id', clubIds)
      : Promise.resolve({ data: [], error: null }),
    seasonIds.length
      ? supabase.from('league_seasons').select('id, label').in('id', seasonIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  throwIf(clubsError, 'Could not load player clubs');
  throwIf(seasonsError, 'Could not load player seasons');

  const teamsById = new Map((teams ?? []).map((row) => [row.id, row]));
  const clubsById = new Map((clubs ?? []).map((row) => [row.id, row]));
  const seasonsById = new Map((seasons ?? []).map((row) => [row.id, row]));
  const fixturesById = new Map((fixtures ?? []).map((row) => [row.id, row]));

  const contextByPlayer = new Map();
  for (const lineup of lineups ?? []) {
    const entry = contextByPlayer.get(lineup.player_id) ?? { clubs: new Set(), teams: new Set(), seasons: new Set() };
    const team = teamsById.get(lineup.team_id);
    if (team?.name) entry.teams.add(team.name);
    const club = clubsById.get(team?.league_club_id);
    if (club?.name) entry.clubs.add(club.name);
    const season = seasonsById.get(fixturesById.get(lineup.fixture_id)?.season_id);
    if (season?.label) entry.seasons.add(season.label);
    contextByPlayer.set(lineup.player_id, entry);
  }

  return rows.map((player) => {
    const context = contextByPlayer.get(player.id);
    return {
      ...player,
      clubs: context ? [...context.clubs].sort() : [],
      teams: context ? [...context.teams].sort() : [],
      seasons: context ? [...context.seasons].sort() : [],
    };
  });
};

export const fetchLeaguePlayerSuppressions = async () => {
  const { data, error } = await supabase
    .from('league_player_suppressions')
    .select('id, source_id, source_player_id, note, created_by, created_at')
    .order('created_at', { ascending: false });
  throwIf(error, 'Could not load withheld players');
  const rows = data ?? [];
  const actorIds = [...new Set(rows.map((row) => row.created_by).filter(Boolean))];
  const { data: actors, error: actorsError } = actorIds.length
    ? await supabase.from('players').select('id, first_name, last_name').in('id', actorIds)
    : { data: [], error: null };
  throwIf(actorsError, 'Could not load who added withholdings');
  const actorsById = new Map((actors ?? []).map((row) => [row.id, row]));
  return rows.map((row) => ({
    ...row,
    createdBy: actorsById.get(row.created_by) ?? null,
  }));
};

export const suppressLeaguePlayer = async (leaguePlayerId, note) => {
  const { data, error } = await supabase.rpc('suppress_league_player', {
    p_league_player_id: leaguePlayerId,
    p_note: note || null,
  });
  throwIf(error, 'Could not withhold league player');
  return data;
};

export const unsuppressLeaguePlayer = async (suppressionId) => {
  const { error } = await supabase.rpc('unsuppress_league_player', {
    p_suppression_id: suppressionId,
  });
  throwIf(error, 'Could not restore league player');
};
